import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import registrationImgMobile from '~/assets/images/auth/registration_mobile.png';
import logo from '~/assets/images/auth/topstep_logo_new.svg';
import { EmailAndSocialAuth } from '~/pages/auth/login/components/EmailAndSocialAuth';
import type { SocialProvider } from '~/pages/auth/onboarding/components/SocialLoginButton/SocialLoginButton';
import { redirectToCognitoSocial } from '~/pages/auth/redirectToCognito';
import { EMAIL_REGEX } from '~/utils/email-validation';

/**
 * Form values for the social login landing page
 */
interface SocialLandingFormProps {
  email: string;
}

const initialValues: SocialLandingFormProps = {
  email: '',
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .max(255)
    .matches(EMAIL_REGEX, 'Must be a valid email address'),
});

/**
 * Social login landing page component - the entry point for registration when social login is enabled.
 *
 * This component serves as a streamlined first step in the registration flow, allowing users to
 * choose between email registration or OAuth-based social login (Google, Apple, Facebook).
 * It simplifies the initial decision point before committing to the full registration form.
 *
 * ## Business Rules & Context
 *
 * **Feature Flag Requirement:**
 * - Only shown when `enable-social-login` LaunchDarkly feature flag is enabled
 * - Accessed via the route wrapper `SocialLandingRoute` which checks feature flags
 * - Falls back to standard registration flow if feature flag is disabled
 *
 * **User Flow Options:**
 * 1. **Email Registration**: User enters email → navigates to `/register/email` with email pre-filled
 * 2. **Social Login**: User clicks social provider button → OAuth redirect (TODO: not yet implemented)
 *
 * **Validation:**
 * - Email format validation (standard email regex)
 * - Maximum length: 255 characters
 * - Required field validation
 * - Real-time error display on blur
 *
 * ## Supported Social Providers
 *
 * - **Google**: OAuth 2.0 authentication
 * - **Facebook**: Facebook Login
 * - **Apple**: Sign in with Apple
 *
 * Note: Social login OAuth flows are placeholders and need implementation.
 *
 * ## Navigation & Routing
 *
 * **Entry Route:**
 * - `/register` - When social login feature flag is enabled
 *
 * **Exit Routes:**
 * - Email path: `/register/email` with `location.state = { email: string }`
 * - Social OAuth: (TODO) Redirects to provider OAuth flow
 * - Login link: `/login` for existing users
 *
 * **Location State Shape (Output):**
 * ```typescript
 * {
 *   email: string  // User-entered email, passed to registration form
 * }
 * ```
 *
 * ## Component Props
 *
 * This component accepts no props. All data is managed internally via:
 * - Form state (Formik)
 * - Navigation (React Router)
 * - Loading states (local useState)
 *
 * ## Public API Surface
 *
 * **Types:**
 * - `SocialLandingFormProps` - Form values interface (internal)
 *
 * **Events:**
 * - `handleEmailSubmit` - Validates email and navigates to `/register/email`
 * - `handleSocialLogin` - Placeholder for OAuth provider selection
 *
 * ## Usage
 *
 * This component is typically rendered through the routing system with feature flag checks:
 *
 * @example
 * ```tsx
 * // In LoginRoutes.tsx
 * import SocialLandingRoute from '~/components/auth/SocialLandingRoute';
 *
 * {
 *   path: 'register',
 *   element: <SocialLandingRoute />
 * }
 *
 * // SocialLandingRoute checks feature flags and renders:
 * import AuthRegisterSocialLanding from '~/sections/auth/auth-forms/AuthRegisterSocialLanding';
 *
 * <AuthWrapperRegisterNew>
 *   <AuthRegisterSocialLanding />
 * </AuthWrapperRegisterNew>
 *
 * // After email submission, location state is passed:
 * navigate('/register/email', { state: { email: 'user@example.com' } });
 * ```
 *
 * @see {@link SocialLoginButton} for individual OAuth provider buttons
 * @see {@link AuthRegisterCognitoNew} for the full registration form (receives email via state)
 * @see {@link SocialLandingRoute} for feature flag routing logic
 * @see {@link useSocialLoginFlag} for `enable-social-login` feature flag hook
 *
 * @returns React component rendering the social login landing page
 */
const AuthRegisterSocialLanding = () => {
  const navigate = useNavigate();

  const handleEmailSubmit = (values: SocialLandingFormProps) => {
    // Navigate to email registration route with email pre-filled
    navigate('/register/email', { state: { email: values.email } });
  };

  const handleSocialLogin = async (provider: SocialProvider) => {
    const providerMap: Record<SocialProvider, 'Google' | 'Facebook' | 'SignInWithApple'> = {
      google: 'Google',
      facebook: 'Facebook',
      apple: 'SignInWithApple',
    };
    await redirectToCognitoSocial(providerMap[provider]);
  };

  return (
    <div className="flex h-full w-full flex-col justify-center px-4 pt-8 sm:px-0 sm:pt-12 lg:p-12">
      <div className={`flex justify-center sm:hidden`}>
        <img src={registrationImgMobile} alt="Registration" className="w-42" />
      </div>
      {/* Header */}
      <div className="mb-6 text-center sm:text-start">
        <img src={logo} alt="Topstep logo" className="mx-auto mb-6 h-8 w-auto sm:mx-0 lg:hidden" />
        <h1 className="mb-1 text-2xl font-bold text-primary">Sign Up</h1>
        <p className="text-sm text-gray-300">
          Already a Topstep Trader?{' '}
          <Link to="/login" className="font-medium text-utility-blue-500 underline">
            Login
          </Link>
        </p>
      </div>

      <Formik<SocialLandingFormProps>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleEmailSubmit}
      >
        {({ errors, handleBlur, isSubmitting, setFieldValue, touched, values }) => (
          <Form>
            <EmailAndSocialAuth
              email={values.email}
              onEmailChange={(value) => setFieldValue('email', value)}
              onEmailBlur={handleBlur('email')}
              isSubmitting={isSubmitting}
              emailError={errors.email}
              emailTouched={touched.email}
              onContinue={() => {}} // Form submission is handled by Formik
              onSocialLogin={handleSocialLogin}
              socialAction="continue"
            />

            <p className="hidden pt-4 text-sm text-tertiary sm:block">
              By signing up, you agree to our{' '}
              <a
                href="https://www.topstep.com/terms-of-use/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-utility-blue-500 underline"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href="https://www.topstep.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-utility-blue-500 underline"
              >
                Privacy Policy
              </a>
              .
            </p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AuthRegisterSocialLanding;
